import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Typography,
  Input,
  List,
  Spin,
  message,
  Avatar,
  Space,
  Badge,
  Tooltip,
  Dropdown,
  Modal,
  Select,
  Tag
} from 'antd';
import {
  UserOutlined,
  SendOutlined,
  RobotOutlined,
  WifiOutlined,
  ClearOutlined,
  MoreOutlined,
  PlusOutlined,
  DeleteOutlined,
  FileTextOutlined,
  ExperimentOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import io from 'socket.io-client';
import authService from '../../services/authService';
import './AgentPage.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// WebSocket URL - 使用与ChatModal相同的配置
const WEBSOCKET_URL = "https://api.scai.sh";

// 🆕 Knowledge Base Module Component
const KnowledgeBaseModule = ({ onAddDocument, documents, onRemoveDocument, loading }) => {
  const [inputValue, setInputValue] = useState("");
  const [inputType, setInputType] = useState("doi"); // "doi", "irys_url", "irys_hash"

  const handleAdd = () => {
    if (!inputValue.trim()) {
      message.warning("Please enter a valid input");
      return;
    }

    let processedValue = inputValue.trim();

    // 处理不同类型的输入
    if (inputType === "irys_hash") {
      // 如果是IRYS哈希，转换为完整URL
      processedValue = `http://gateway.irys.xyz/${processedValue}`;
    } else if (inputType === "irys_url") {
      // 验证IRYS URL格式
      if (!processedValue.includes("gateway.irys.xyz") && !processedValue.includes("irys.xyz")) {
        message.error("Please enter a valid IRYS URL");
        return;
      }
    }

    onAddDocument(processedValue, inputType);
    setInputValue("");
  };

  const getPlaceholder = () => {
    switch (inputType) {
      case "doi":
        return "Enter DOI (e.g., 10.1000/example)";
      case "irys_url":
        return "Enter IRYS URL (e.g., http://gateway.irys.xyz/hash)";
      case "irys_hash":
        return "Enter IRYS hash (e.g., ABC123...)";
      default:
        return "Enter document identifier";
    }
  };

  return (
    <Card
      title={
        <Space>
          <FileTextOutlined style={{ color: '#1890ff' }} />
          <span>Knowledge Base</span>
        </Space>
      }
      style={{ marginBottom: '20px' }}
      className="knowledge-base-card"
    >
      <div style={{ marginBottom: '16px' }}>
        <Text type="secondary">
          Add documents to your knowledge base for enhanced AI analysis and comparison.
        </Text>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <Select
          value={inputType}
          onChange={setInputType}
          style={{ width: 120 }}
          options={[
            { value: "doi", label: "DOI" },
            { value: "irys_url", label: "IRYS URL" },
            { value: "irys_hash", label: "IRYS Hash" }
          ]}
        />
        <Input
          placeholder={getPlaceholder()}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onPressEnter={handleAdd}
          style={{ flex: 1, minWidth: '200px' }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          loading={loading}
          disabled={!inputValue.trim()}
        >
          Add
        </Button>
      </div>

      {documents.length > 0 && (
        <div>
          <Text strong style={{ marginBottom: '8px', display: 'block' }}>
            Documents in Knowledge Base ({documents.length}):
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {documents.map((doc, index) => (
              <Tag
                key={index}
                closable
                onClose={() => onRemoveDocument(doc.doi)}
                color="blue"
                style={{ marginBottom: '4px' }}
              >
                {doc.doi.length > 30 ? `${doc.doi.substring(0, 30)}...` : doc.doi}
              </Tag>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

const AgentPage = () => {
  const { user, isAuthenticated, getBackendToken } = useAuth();

  // Chat state
  const [socket, setSocket] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I'm SCAI Assistant. I can help you with academic research, paper analysis, and scientific questions. You can also add multiple papers for comparative analysis. How can I assist you today?",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'connecting', 'connected', 'disconnected'

  // 🆕 流式响应状态
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState(""); // 当前正在接收的流式消息
  const [isStreaming, setIsStreaming] = useState(false); // 是否正在接收流式响应

  // Multi-DOI RAG state
  const [ragPapers, setRagPapers] = useState([]); // 当前RAG系统中的论文列表
  const [ragStatus, setRagStatus] = useState(null); // RAG系统状态
  const [addingDoi, setAddingDoi] = useState(false); // 添加DOI状态

  // 🆕 用户头像状态
  const [userAvatar, setUserAvatar] = useState(null);

  // Refs
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 🆕 获取用户头像
  useEffect(() => {
    const loadUserAvatar = async () => {
      if (!isAuthenticated || !user) return;

      try {
        // 使用authService获取用户资料
        const userInfo = await authService.getUserInfo();
        if (userInfo?.avatar_url) {
          setUserAvatar(userInfo.avatar_url);
          return;
        }

        // 如果没有，尝试从localStorage获取
        const localProfileData = localStorage.getItem(`scai_profile_${user.user_id}`);
        if (localProfileData) {
          const profileData = JSON.parse(localProfileData);
          if (profileData.avatarUrl) {
            setUserAvatar(profileData.avatarUrl);
            return;
          }
        }

        // 如果都没有，使用默认头像
        setUserAvatar(null);
      } catch (error) {
        console.warn('Failed to load user avatar:', error);
        setUserAvatar(null);
      }
    };

    loadUserAvatar();
  }, [isAuthenticated, user]);

  // Initialize WebSocket connection when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.user_id) {
      initializeConnection();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, user]);

  const initializeConnection = async () => {
    try {
      setConnectionStatus('connecting');
      const token = await getBackendToken();
      if (!token) {
        setError("Authentication failed, please login again");
        setConnectionStatus('disconnected');
        return;
      }

      console.log("Connecting to WebSocket for general chat...");

      const newSocket = io(WEBSOCKET_URL, {
        auth: { token },
        transports: ["websocket"],
        reconnectionAttempts: 0, // 禁用自动重连
        forceNew: true,
        timeout: 10000,
        // 🆕 心跳配置 - 防止 ping timeout
        pingInterval: 25000, // 每 25 秒发送一次 ping
        pingTimeout: 60000, // 60 秒内没有收到 pong 才断开
        upgrade: false, // 禁用传输升级，保持 WebSocket
        rememberUpgrade: false,
      });

      socketRef.current = newSocket;
      setSocket(newSocket);
      setupSocketListeners(newSocket);
    } catch (error) {
      console.error("Failed to connect:", error);
      setError("Connection failed, please check network or login again");
      setConnectionStatus('disconnected');
    }
  };

  const setupSocketListeners = (socket) => {
    socket.on("connect", () => {
      console.log("✅ WebSocket connected for general chat");
      setConnectionStatus('connected');
      setError(null);
      setLoading(false);

      // 🆕 使用正确的连接方式 - 建立通用聊天会话
      const generalSessionId = `general_${Date.now()}`;
      setSessionId(generalSessionId);

      // 建立一个空的RAG会话用于通用聊天
      socket.emit("connect_rag", {
        session_id: generalSessionId,
        ids: "", // 空的DOI列表表示通用聊天
        source: "auto"
      });
    });

    socket.on("session_connected", (data) => {
      console.log("📋 Session connected:", data);
      setSessionId(data.session_id || sessionId);
      setLoading(false);
      setAddingDoi(false);

      // 处理RAG状态和DOI信息
      if (data.status) {
        setRagStatus(data.status);
      }
      if (data.dois && data.dois.length > 0) {
        setRagPapers(data.dois.map(doi => ({ doi, status: 'loaded' })));
        setMessages(prev => [...prev, {
          role: "assistant",
          content: `📄 Successfully loaded ${data.dois.length} paper(s): ${data.dois.join(', ')}. You can now ask questions about these papers.`
        }]);
      }
      if (data.failed_dois && data.failed_dois.length > 0) {
        setError(`Failed to load papers: ${data.failed_dois.join(', ')}`);
        message.error(`Failed to load papers: ${data.failed_dois.join(', ')}`);
      }

      // 如果是通用聊天会话，显示欢迎消息
      if (data.type === "multi_doi_rag" && (!data.dois || data.dois.length === 0)) {
        console.log("✅ General chat session ready");
      }
    });

    // 🆕 DOI添加成功
    socket.on("doi_added", (data) => {
      console.log("✅ DOI added:", data);
      setRagPapers(prev => {
        // 避免重复添加
        const exists = prev.some(paper => paper.doi === data.doi);
        if (exists) return prev;
        return [...prev, { doi: data.doi, status: 'loaded' }];
      });
      setRagStatus(data.status);
      setAddingDoi(false);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `📄 Successfully added paper: ${data.doi}. You can now ask questions about this paper along with others in the collection.`
      }]);
      message.success(`Added paper: ${data.doi}`);
    });

    // 🆕 DOI添加失败
    socket.on("doi_add_failed", (data) => {
      console.log("❌ DOI add failed:", data);
      setError(`Failed to add paper ${data.doi}: ${data.error}`);
      setAddingDoi(false);
      message.error(`Failed to add paper: ${data.error || 'Unknown error'}`);
    });

    // 🆕 DOI移除成功
    socket.on("doi_removed", (data) => {
      console.log("🗑️ DOI removed:", data);
      setRagPapers(prev => prev.filter(paper => paper.doi !== data.doi));
      setRagStatus(data.status);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `🗑️ Removed paper: ${data.doi} from the analysis collection.`
      }]);
    });

    // 🆕 DOI移除失败
    socket.on("doi_remove_failed", (data) => {
      console.log("❌ DOI remove failed:", data);
      setError(`Failed to remove paper ${data.doi}: ${data.error}`);
    });

    // 🆕 RAG状态更新
    socket.on("rag_status", (data) => {
      console.log("📊 RAG status:", data);
      setRagStatus(data.status);
      if (data.status && data.status.dois) {
        setRagPapers(data.status.dois.map(doi => ({ doi, status: 'loaded' })));
      }
    });

    // 🆕 处理后端response事件（主要事件）
    socket.on("response", (data) => {
      setLoading(false);

      if (data.chunk) {
        // 流式响应处理
        setIsStreaming(currentStreaming => {
          if (!currentStreaming) {
            // 开始新的流式响应
            setCurrentStreamingMessage(data.chunk);
            setMessages((prev) => [...prev, { role: "assistant", content: data.chunk, isStreaming: true }]);
            return true;
          } else {
            // 继续流式响应，更新最后一条消息
            setCurrentStreamingMessage(prev => prev + data.chunk);
            setMessages((prev) => {
              const newMessages = [...prev];
              const lastMessage = newMessages[newMessages.length - 1];
              if (lastMessage && lastMessage.role === "assistant" && lastMessage.isStreaming) {
                lastMessage.content += data.chunk;
              }
              return newMessages;
            });
            return true;
          }
        });

        // 检查是否完成（progress = 1 或者有完成标志）
        if (data.progress === 1 || data.finished) {
          setIsStreaming(false);
          setCurrentStreamingMessage("");
          setLoading(false);

          // 移除流式标志并添加额外信息
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === "assistant" && lastMessage.isStreaming) {
              delete lastMessage.isStreaming;

              // 添加多DOI分析信息
              if (data.dois && data.dois.length > 0) {
                lastMessage.content += `\n\n*Based on analysis of ${data.dois.length} paper(s): ${data.dois.join(', ')}*`;
              }
              if (data.total_documents) {
                lastMessage.content += `\n*Total documents analyzed: ${data.total_documents}*`;
              }
            }
            return newMessages;
          });
        }
      } else {
        // 非流式响应（完整消息）
        let responseContent = data.response || data.message || data.chunk || "";

        // 如果是多DOI响应，添加额外信息
        if (data.dois && data.dois.length > 0) {
          responseContent += `\n\n*Based on analysis of ${data.dois.length} paper(s): ${data.dois.join(', ')}*`;
        }
        if (data.total_documents) {
          responseContent += `\n*Total documents analyzed: ${data.total_documents}*`;
        }

        setMessages((prev) => [...prev, { role: "assistant", content: responseContent }]);
        setLoading(false);
        setIsStreaming(false);
      }
    });

    // 🆕 处理response_complete事件
    socket.on("response_complete", () => {
      setLoading(false);
      setIsStreaming(false);
    });

    // 🆕 移除重复的response事件监听器，只保留chat_response
    // response事件监听器已移除，避免重复处理

    socket.on("error", (error) => {
      console.error("❌ WebSocket error:", error);
      setError(error.message || "Connection error occurred");
      setLoading(false);
      setAddingDoi(false);
    });

    socket.on("disconnect", (reason) => {
      console.log("🔌 WebSocket disconnected:", reason);
      setConnectionStatus('disconnected');
      if (reason === "io server disconnect") {
        setError("Server disconnected, please refresh the page");
      }
    });

    socket.on("connect_error", (error) => {
      console.error("❌ Connection error:", error);
      setConnectionStatus('disconnected');
      setError("Failed to connect to SCAI Agent service");
      setLoading(false);
    });
  };

  const sendMessage = () => {
    if (!isAuthenticated) {
      setError("Please login first to send messages");
      return;
    }

    if (!query.trim()) {
      setError("Please enter your question");
      return;
    }

    if (!socket || !socket.connected) {
      setError("Connection not ready, please wait...");
      return;
    }

    if (!sessionId) {
      setError("Session not ready, please wait...");
      return;
    }

    // 🆕 重置流式状态
    setIsStreaming(false);
    setCurrentStreamingMessage("");
    setLoading(true);
    setError(null);
    setMessages((prev) => [...prev, { role: "user", content: query }]);

    // 🆕 使用正确的chat事件格式
    const messageData = {
      session_id: sessionId,
      query: query
    };

    socket.emit("chat", messageData);
    setQuery("");
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleReconnect = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setSocket(null);
    setSessionId(null);
    setError(null);
    initializeConnection();
  };

  const handleClearChat = () => {
    Modal.confirm({
      title: 'Clear Chat History',
      content: 'Are you sure you want to clear all chat messages? This action cannot be undone.',
      okText: 'Clear',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk() {
        setMessages([
          {
            role: "assistant",
            content: "Hello! I'm SCAI Assistant. I can help you with academic research, paper analysis, and scientific questions. You can also add multiple papers for comparative analysis. How can I assist you today?",
          },
        ]);
        message.success('Chat history cleared');
      },
    });
  };

  // 🆕 添加文档到知识库（支持DOI、IRYS URL、IRYS哈希）
  const handleAddDocument = (value, type) => {
    if (!value.trim()) {
      setError("Please enter a valid document identifier");
      return;
    }

    if (!socket || !socket.connected) {
      setError("Connection not ready, please wait...");
      return;
    }

    setAddingDoi(true);
    setError(null);

    // 根据类型处理输入值
    let processedValue = value.trim();
    let sourceType = 'auto';

    if (type === 'irys_url' || type === 'irys_hash') {
      sourceType = 'irys';
      // 如果是IRYS哈希，确保转换为完整URL
      if (type === 'irys_hash' && !processedValue.includes('gateway.irys.xyz')) {
        processedValue = `https://gateway.irys.xyz/${processedValue}`;
      }
    }

    console.log('Adding document:', { value: processedValue, type, sourceType });

    // 如果还没有RAG会话或者当前是通用聊天会话，建立新的多DOI RAG会话
    if (!sessionId || ragPapers.length === 0) {
      const newSessionId = `rag_${Date.now()}`;
      setSessionId(newSessionId);

      console.log('Creating new RAG session with document:', processedValue);
      socket.emit('connect_rag', {
        session_id: newSessionId,
        ids: processedValue,
        source: sourceType
      });
    } else {
      // 向现有会话添加文档
      console.log('Adding document to existing session:', sessionId);
      socket.emit('add_doi', {
        session_id: sessionId,
        doi: processedValue,
        source: sourceType
      });
    }
  };



  // 🆕 从RAG系统移除DOI
  const handleRemoveDoi = (doi) => {
    if (!socket || !socket.connected) {
      setError("Connection not ready");
      return;
    }

    socket.emit('remove_doi', {
      session_id: sessionId,
      doi: doi
    });
  };

  // 🆕 获取RAG状态
  const handleGetRagStatus = () => {
    if (!socket || !socket.connected || !sessionId) {
      setError("Connection or session not ready");
      return;
    }

    socket.emit('get_rag_status', {
      session_id: sessionId
    });
  };

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className={`search-page light-theme`}>
          <div className="hero-section1">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="hero-content"
            >
              <Title level={1} className="hero-title" style={{ color: "#fff" }}>
                SCAI Agent
              </Title>
              <Paragraph className="hero-subtitle">
                Your AI Research Assistant for Academic Excellence
              </Paragraph>
            </motion.div>
          </div>
          <div
            style={{
              padding: "2rem",
              maxWidth: "1200px",
              margin: "0 auto",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <div
              className="scholar-container"
              style={{
                width: "100%",
                padding: "2rem",
              }}
            >
              <div
                style={{
                  textAlign: "center",
                }}
              >
                <RobotOutlined
                  style={{
                    fontSize: 64,
                    color: "#fff",
                    marginBottom: 7,
                  }}
                />
                <Title
                  level={2}
                  style={{
                    color: "#fff",
                    marginBottom: 16,
                  }}
                >
                  Login Required
                </Title>
                <Text
                  style={{
                    color: "#ccc",
                    fontSize: 16,
                    marginBottom: 24,
                    maxWidth: 500,
                    margin: "0 auto 24px",
                    display: "block",
                  }}
                >
                  Please sign in to access SCAI Agent and start your AI-powered research conversations.
                </Text>

                <Button
                  className="modern-btn modern-btn-primary"
                  type="primary"
                  size="large"
                  style={{
                    height: 48,
                    paddingLeft: 32,
                    paddingRight: 32,
                    fontSize: 16,
                  }}
                  onClick={() => {
                    const loginButton = document.querySelector(".login-btn");
                    if (loginButton) {
                      loginButton.click();
                    } else {
                      message.info("Please use the login button in the top right corner to log in");
                    }
                  }}
                >
                  Sign In to Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`search-page light-theme`}>
        <div className="hero-section1">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-content"
          >
            <Title level={1} className="hero-title" style={{ color: "#fff" }}>
              SCAI Agent
            </Title>
            <Paragraph className="hero-subtitle">
              Your AI Research Assistant for Academic Excellence
            </Paragraph>
          </motion.div>
        </div>
        <div className="agent-page">
          <div className="agent-container">
            {/* 🆕 Knowledge Base Module */}
            <KnowledgeBaseModule
              onAddDocument={handleAddDocument}
              documents={ragPapers}
              onRemoveDocument={handleRemoveDoi}
              loading={addingDoi}
            />

            <Card className="chat-card">
              <div className="chat-header">
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space>
                    <Avatar icon={<RobotOutlined />} style={{ backgroundColor: '#1890ff', marginRight: '10px' }} />
                    <div>
                      <Title level={4} style={{ margin: 0 }}><span style={{ color: '#000' }}>SCAI Agent</span></Title>
                      <Space>
                        <Text type="secondary"><span style={{ color: '#000' }}>AI Research Assistant</span></Text>
                        <Tooltip title={
                          connectionStatus === 'connected' ? 'Connected' :
                            connectionStatus === 'connecting' ? 'Connecting...' : 'Disconnected'
                        }>
                          <Badge
                            status={
                              connectionStatus === 'connected' ? 'success' :
                                connectionStatus === 'connecting' ? 'processing' : 'error'
                            }
                            text={
                              connectionStatus === 'connected' ? 'Online' :
                                connectionStatus === 'connecting' ? 'Connecting' : 'Offline'
                            }
                            style={{ color: 'rgba(255, 255, 255, 0.8)' }}
                          />
                        </Tooltip>
                      </Space>
                    </div>
                  </Space>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'clear',
                          label: 'Clear Chat History',
                          icon: <ClearOutlined />,
                          onClick: handleClearChat,
                        },
                        {
                          key: 'reconnect',
                          label: 'Reconnect',
                          icon: <WifiOutlined />,
                          onClick: handleReconnect,
                          disabled: connectionStatus === 'connected',
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button
                      type="text"
                      icon={<MoreOutlined />}
                      style={{ color: 'rgba(255, 255, 255, 0.8)' }}
                    />
                  </Dropdown>
                </Space>
              </div>

              {/* 🆕 论文管理面板 */}
              {ragPapers.length > 0 && (
                <div className="papers-panel">
                  <div className="papers-header">
                    <Space>
                      <ExperimentOutlined style={{ color: '#1890ff' }} />
                      <Text strong>Research Papers ({ragPapers.length})</Text>
                      {ragStatus && (
                        <Tooltip title={`Total documents: ${ragStatus.total_documents || 0}, Index ready: ${ragStatus.index_ready ? 'Yes' : 'No'}`}>
                          <InfoCircleOutlined style={{ color: '#666' }} />
                        </Tooltip>
                      )}
                    </Space>
                  </div>
                  <div className="papers-list">
                    {ragPapers.map((paper) => (
                      <div key={paper.doi} className="paper-item">
                        <Space>
                          <FileTextOutlined style={{ color: '#52c41a' }} />
                          <Text className="paper-doi">{paper.doi}</Text>
                          <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemoveDoi(paper.doi)}
                            style={{ color: '#ff4d4f' }}
                            title="Remove paper"
                          />
                        </Space>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="chat-messages">
                <List
                  dataSource={messages}
                  renderItem={(item) => (
                    <List.Item
                      className={`message-item ${item.role} ${item.isStreaming ? 'streaming' : ''}`}
                    >
                      <div className="message-content">
                        <Avatar
                          src={item.role === "user" ? userAvatar : "/apple-touch-icon.png"}
                          icon={
                            item.role === "user"
                              ? (userAvatar ? null : <UserOutlined />)
                              : null
                          }
                          style={{
                            backgroundColor: item.role === "user" ? '#52c41a' : '#1890ff',
                            marginBottom: 'auto',
                            width: '40px',
                            height: '40px',
                            flexShrink: 0
                          }}
                        />
                        <div className="message-text">
                          <ReactMarkdown>{item.content || ""}</ReactMarkdown>
                          {/* {item.isStreaming && (
                            <span className="streaming-indicator">
                              <Spin size="small" style={{ marginLeft: '8px' }} />
                            </span>
                          )} */}
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
                {loading && (
                  <div className="loading-message">
                    <Spin tip="SCAI Agent is thinking..." />
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>





              <div className="chat-input">
                {error && (
                  <div className="error-message">
                    <Space>
                      <Text type="danger">{error}</Text>
                      {connectionStatus === 'disconnected' && (
                        <Button
                          size="small"
                          type="link"
                          onClick={handleReconnect}
                          icon={<WifiOutlined />}
                        >
                          Reconnect
                        </Button>
                      )}
                    </Space>
                  </div>
                )}
                <div className="input-container">
                  <TextArea
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Ask SCAI Agent anything about research, papers, or science..."
                    disabled={loading || connectionStatus !== 'connected'}
                    onPressEnter={handleKeyPress}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    className="message-input"
                  />
                  <Button
                    type="primary"
                    onClick={sendMessage}
                    disabled={loading || connectionStatus !== 'connected' || !query.trim()}
                    loading={loading}
                    icon={<SendOutlined />}
                    className="send-button"
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AgentPage;
